import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import isPlainObject from 'lodash/isPlainObject'
import omitBy from 'lodash/omitBy'

const LIST_STATE_STORAGE_VERSION = 'v1'

export function pruneFilterValue(value: unknown): unknown {
  if (isNil(value) || value === '') return undefined

  if (Array.isArray(value)) {
    const prunedArray = value
      .map(pruneFilterValue)
      .filter((item) => item !== undefined)

    return prunedArray.length > 0 ? prunedArray : undefined
  }

  if (isPlainObject(value)) {
    const prunedObject = Object.fromEntries(
      Object.entries(value)
        .map(([key, val]) => [key, pruneFilterValue(val)])
        .filter(([, val]) => val !== undefined)
    )

    return Object.keys(prunedObject).length > 0 ? prunedObject : undefined
  }

  return value
}

export function pruneFilterBy<F extends Record<string, unknown>>(
  filterBy: F | undefined
): F | undefined {
  if (!isPlainObject(filterBy)) return undefined

  const pruned = pruneFilterValue(filterBy)
  return isPlainObject(pruned) ? (pruned as F) : undefined
}

export function omitDefaults<
  TState extends object,
  TDefaults extends Partial<TState>,
>(state: TState, defaults: TDefaults): Partial<TState> {
  const sparse = omitBy(
    state as Record<string, unknown>,
    (value, key) =>
      value === undefined ||
      isEqual(value, (defaults as Record<string, unknown>)[key])
  )

  return sparse as Partial<TState>
}

export function buildStorageKey(
  orgId: string | undefined,
  routeId: string,
  scope?: string,
  tableId?: string
): string {
  const orgSegment = orgId && orgId.trim() ? orgId : 'global'
  const routeSegment = routeId && routeId.trim() ? routeId : 'unknown-route'
  const scopeSegment = scope && scope.trim() ? scope : 'default-scope'
  const tableSegment = tableId && tableId.trim() ? tableId : 'default'

  return `${LIST_STATE_STORAGE_VERSION}::${orgSegment}::${routeSegment}::${scopeSegment}::${tableSegment}`
}
